import React, { useState, useRef, useEffect } from "react";
import { BsChatDotsFill } from "react-icons/bs";
import { IoClose, IoMenu } from "react-icons/io5";
import { FiEdit, FiCheck, FiAlertCircle, FiLoader, FiZap } from "react-icons/fi"; // Assuming react-icons/fi is available or use generic
import { Snackbar, Alert } from '@mui/material';
import { useNavigate } from "react-router-dom";

import { callAzureOpenAI } from "../../api/chatbot";
import { voteApi, multipartVoteApi, getUserId, getToken, getAzureCredentials, preparePromptMainBallot, preparePromptSubBallot, buildValidationPrompt, saveChatbotHistory, getChatbotHistory, getAllChatsByUser, getAllVotesWithFilters } from "../../api";
import "./Chatbot.css";

const initialGreeting = {
  role: "assistant",
  content: "Hello! How can I help you today?",
};

const Chatbot = ({ onBallotCreated, isPageMode = false }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([initialGreeting]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [trendingQuestions, setTrendingQuestions] = useState([]);
  const [isTrendingLoading, setIsTrendingLoading] = useState(false);
  const [selectedBallotId, setSelectedBallotId] = useState(null);
  const [editingBallotId, setEditingBallotId] = useState(null);
  const [editingBallot, setEditingBallot] = useState(null);
  const [parentVoteId, setParentVoteId] = useState(null);
  const [showSubBallotPrompt, setShowSubBallotPrompt] = useState(false);
  const [isGeneratingSubBallots, setIsGeneratingSubBallots] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaType, setMediaType] = useState(null); // 'image', 'video', or 'file'
  const [mediaPreview, setMediaPreview] = useState(null);
  const [savedBallotIds, setSavedBallotIds] = useState(new Set()); // Track saved ballot IDs

  const [parentVoteOptions, setParentVoteOptions] = useState([]); // Store options of the saved parent ballot
  const [azureCredentials, setAzureCredentials] = useState(null); // Store fetched Azure credentials
  const [draggedOptionIndex, setDraggedOptionIndex] = useState(null); // Track dragged option index
  const [sessionId, setSessionId] = useState(null); // Store chatbot session ID

  // Bulk sub-ballot editing state
  const [isEditingSubBallots, setIsEditingSubBallots] = useState(false); // Toggle edit mode for all sub-ballots
  const [editableSubBallots, setEditableSubBallots] = useState([]); // Store editable copies of sub-ballots
  const [subBallotsSaved, setSubBallotsSaved] = useState(false); // Track if sub-ballots have been saved
  const [isSavingSubBallots, setIsSavingSubBallots] = useState(false); // Loading state for bulk save

  // Location state for address autocomplete
  const [locationDisplay, setLocationDisplay] = useState(''); // e.g. "San Francisco, CA, USA"
  const [locationDetails, setLocationDetails] = useState({
    city: '',
    state: '',
    country: '',
    zip: ''
  });
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);

  // Sidebar/History state
  const [showSidebar, setShowSidebar] = useState(false);
  const [chatHistoryList, setChatHistoryList] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState(""); // State for save validation error

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success' | 'error' | 'warning' | 'info'
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const bodyOverflowRef = useRef(null);
  const locationInputRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const geocoderRef = useRef(null);
  const validationTimeoutRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Prevent background (page) scrolling while chatbot is open
  useEffect(() => {
    if (isOpen) {
      bodyOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    } else if (bodyOverflowRef.current !== null) {
      document.body.style.overflow = bodyOverflowRef.current;
    }

    return () => {
      if (bodyOverflowRef.current !== null) {
        document.body.style.overflow = bodyOverflowRef.current;
      }
    };
  }, [isOpen]);

  // Listen for open-chatbot custom events from Sidebar and check query params
  useEffect(() => {
    const handleOpenChatbot = () => {
      setIsOpen(true);
    };
    window.addEventListener("open-chatbot", handleOpenChatbot);

    const params = new URLSearchParams(window.location.search);
    if (params.get("openBallotCreator") === "true" || params.get("openChatbot") === "true") {
      setIsOpen(true);
      const cleanPath = window.location.pathname;
      window.history.replaceState({}, "", cleanPath);
    }

    return () => {
      window.removeEventListener("open-chatbot", handleOpenChatbot);
      window.dispatchEvent(new CustomEvent("chatbot-open-change", { detail: { isOpen: false } }));
    };
  }, []);

  // Broadcast chatbot open state changes
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("chatbot-open-change", { detail: { isOpen } }));
  }, [isOpen]);

  // Fetch 3 trending questions (used on open + clear)
  const loadTrendingQuestions = async () => {
    try {
      setIsTrendingLoading(true);
      setTrendingQuestions([]);
    } catch (error) {
      console.error("Error fetching trending questions:", error);
      setTrendingQuestions([]);
    } finally {
      setIsTrendingLoading(false);
    }
  };

  // Fetch trending questions whenever chatbot opens or in page mode
  useEffect(() => {
    if (isOpen || isPageMode) {
      loadTrendingQuestions();
      // Fetch Azure credentials if not already fetched
      if (!azureCredentials) {
        fetchAzureCredentials();
      }
    }
  }, [isOpen, isPageMode]);

  const fetchAzureCredentials = async () => {
    try {
      const response = await getAzureCredentials();
      if (response && response.success && response.data) {
        setAzureCredentials(response.data);
      } else {
        console.error("Failed to fetch Azure credentials");
      }
    } catch (error) {
      console.error("Error fetching Azure credentials:", error);
    }
  };

  const fetchChatHistoryList = async () => {
    const userId = getUserId();
    if (!userId) return;

    try {
      setIsHistoryLoading(true);
      const response = await getAllChatsByUser(userId);
      if (response && response.success && Array.isArray(response.data)) {
        setChatHistoryList(response.data);
      }
    } catch (error) {
      console.error("Error fetching chat history list:", error);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const toggleChatbot = () => {
    setIsOpen(!isOpen);
  };

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
    if (!showSidebar) {
      fetchChatHistoryList();
    }
  };

  const handleNewChat = () => {
    handleClearChat();
    setShowSidebar(false);
  };

  const handleSelectSession = async (session) => {
    const userId = getUserId();
    if (!userId || !session.sessionId) return;

    try {
      setIsLoading(true);
      const historyData = await getChatbotHistory(userId, session.sessionId);

      if (historyData && historyData.success && Array.isArray(historyData.data)) {
        console.log("📜 Loading session history, data items:", historyData.data.length);
        // Reconstruct messages from history
        const reconstructedMessages = [initialGreeting];
        const savedBallots = new Set(); // Track which ballots are saved

        const sortedHistory = [...historyData.data].sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));

        sortedHistory.forEach((item, idx) => {
          try {
            console.log(`Processing history item ${idx}:`, item);

            // Try to parse as JSON first
            try {
              const content = JSON.parse(item.messageText);
              console.log("Parsed as JSON:", content);

              if (content.user) {
                reconstructedMessages.push({ role: "user", content: content.user, fromHistory: true });
              }

              if (content.ai || content.ballots) {
                // Determine the ballots and saved status
                let ballotsToUse = content.ballots || [];
                let rawText = content.ai || "";

                // If we have ballots, mark them in savedBallots set
                ballotsToUse.forEach((ballot, bIdx) => {
                  if (ballot.isSaved) {
                    const ballotId = `msg-${reconstructedMessages.length}-ballot-${bIdx}`;
                    savedBallots.add(ballotId);
                  }
                });

                // Reconstruct the parsed object
                // If we have ai text but no structured ballots, we parse on the fly (legacy)
                let finalParsed = null;
                if (ballotsToUse.length > 0) {
                  finalParsed = {
                    text: rawText,
                    ballots: ballotsToUse,
                    hasBallots: true
                  };
                } else if (rawText) {
                  // If it's just raw AI text, parse it to see if it has ballots
                  finalParsed = parseBallotResponse(rawText);
                }

                const assistantMessage = {
                  role: "assistant",
                  content: rawText,
                  historyId: item.historyId,
                  fromHistory: true
                };

                if (finalParsed && finalParsed.hasBallots) {
                  assistantMessage.parsed = finalParsed;
                }

                reconstructedMessages.push(assistantMessage);
              }
            } catch (jsonError) {
              // If it's not JSON, it's plain text
              reconstructedMessages.push({
                role: "assistant",
                content: item.messageText,
                historyId: item.historyId,
                fromHistory: true
              });
            }
          } catch (e) {
            console.error("Error parsing message:", e);
          }
        });

        console.log("📨 Total messages reconstructed:", reconstructedMessages.length);
        console.log("💾 Saved ballots:", savedBallots);
        console.log("📦 All reconstructed messages:", reconstructedMessages);

        setMessages(reconstructedMessages);
        setSavedBallotIds(savedBallots); // Update the saved ballots state
        setSubBallotsSaved(false); // Reset for new session
        setSessionId(session.sessionId);
        setShowSidebar(false);
      }
    } catch (error) {
      console.error("Error loading session history:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const parseBallotResponse = (text) => {
    const ballots = [];

    // ── Step 1: Split into per-ballot segments ─────────────────────────────────
    // Strategy A: AI used "Ballot X:" / "Ballot X." headers → split on those
    // Strategy B: No headers → every line starting with "1." begins a new ballot
    let segments = [];

    const hasBallotHeaders = /Ballot\s+\d+/i.test(text);

    if (hasBallotHeaders) {
      // Split on "Ballot 1:", "Ballot 2:", etc.
      segments = text
        .split(/Ballot\s+\d+[:\.]?\s*/i)
        .map(s => s.trim())
        .filter(Boolean);
    } else {
      // Split on every line that starts with "1." (start of a new ballot block)
      // Use multiline lookahead so we keep the "1." in each segment
      segments = text
        .split(/(?=^1\.\s)/m)
        .map(s => s.trim())
        .filter(s => /^1\.\s/.test(s));

      // Fallback: blank-line separation
      if (segments.length <= 1) {
        segments = text
          .split(/\n{2,}/)
          .map(s => s.trim())
          .filter(s => /^1\.\s/.test(s));
      }
    }

    // ── Step 2: Parse each segment independently ───────────────────────────────
    segments.forEach((segment) => {
      // Title must be on the first line: "1. <Question text>"
      const titleMatch = segment.match(/^1\.\s+(.+?)(?:\n|$)/m);
      if (!titleMatch) return;

      const title = titleMatch[1].trim();

      // ── Option parsing: match every line that starts with a single capital letter
      // followed by a period, e.g. "A. Some text", "B. Another", "L. Last option"
      // Using multiline flag (m) so ^ anchors to the START OF EACH LINE — completely
      // reliable regardless of how many options there are (4, 6, 12, etc.)
      const options = [];
      const optionLineRegex = /^([A-Z])\.\s+(.+)$/gm;
      let m;
      while ((m = optionLineRegex.exec(segment)) !== null) {
        options.push({
          letter: m[1],
          text: m[2].trim(),
        });
      }

      // Only register as a ballot if at least 2 options were captured
      if (options.length >= 2) {
        ballots.push({
          id: `b${ballots.length + 1}`,
          title,
          question: title,
          options,
          isSaved: false,
        });
      }
    });

    // ── Step 3: Build clean parsedText with __BALLOT_X__ placeholders ──────────
    // Always rebuild from scratch — avoids messy partial replacement bugs
    const parsedText = ballots.map((_, i) => `__BALLOT_${i}__`).join('\n');

    return { text: parsedText.trim(), ballots, hasBallots: ballots.length > 0 };
  };

  const handleKeepBallot = (ballotId, ballot) => {
    // Make the ballot editable
    setEditingBallotId(ballotId);
    setEditingBallot({
      title: ballot.title,
      isSubBallot: !!ballot.isSubBallot,
      parentVoteId: ballot.parentVoteId || null, // Preserve parentVoteId for sub-ballots
      nestedOptionId: ballot.nestedOptionId || null, // Preserve nestedOptionId for sub-ballots
      options: ballot.options.map((opt, idx) => ({
        id: idx + 1,
        letter: opt.letter,
        text: opt.text,
        isOriginal: true // Mark AI-generated options as original (non-editable)
      }))
    });
    // Reset media when starting to edit
    setMediaFile(null);
    setMediaType(null);
    setMediaPreview(null);
    // Reset location when starting to edit
    setLocationDisplay('');
    setLocationDetails({ city: '', state: '', country: '', zip: '' });
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);
  };

  const handleEditTitle = (value) => {
    setEditingBallot(prev => ({ ...prev, title: value }));
  };

  const handleEditOption = (id, value) => {
    setEditingBallot(prev => ({
      ...prev,
      options: prev.options.map(opt =>
        opt.id === id ? {
          ...opt,
          text: value,
          // Reset validation status when text changes
          validationStatus: 'idle',
          validationMessage: null,
          suggestions: []
        } : opt
      )
    }));

    // Debounced validation: trigger after user stops typing for 1.5s
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    if (value.trim()) {
      validationTimeoutRef.current = setTimeout(() => {
        handleValidateOption(id);
      }, 1500);
    }
  };

  const handleValidateOption = async (optionId) => {
    const option = editingBallot.options.find(o => o.id === optionId);
    if (!option || !option.text.trim()) return;

    // Set validating state
    setEditingBallot(prev => ({
      ...prev,
      options: prev.options.map(opt =>
        opt.id === optionId ? { ...opt, isValidating: true } : opt
      )
    }));

    try {
      // 1. Build validation prompt
      const existingOptions = editingBallot.options
        .filter(o => o.id !== optionId && o.text.trim())
        .map(o => o.text.trim());

      const promptResponse = await buildValidationPrompt(
        editingBallot.title,
        existingOptions,
        option.text.trim()
      );

      if (promptResponse && promptResponse.success && promptResponse.data && promptResponse.data.finalPrompt) {
        const finalPrompt = promptResponse.data.finalPrompt;

        // 2. Call Azure OpenAI
        if (!azureCredentials) {
          await fetchAzureCredentials();
        }

        const aiMessages = [{ role: "user", content: finalPrompt }];
        const aiResponseText = await callAzureOpenAI(azureCredentials, aiMessages);

        // 3. Process response
        let validationStatus = 'error';
        let validationMessage = 'Validation failed';
        let suggestions = [];

        const cleanResponse = aiResponseText.trim();

        if (cleanResponse === 'OK' || cleanResponse.startsWith('OK')) {
          validationStatus = 'success';
          validationMessage = null;
        } else {
          // Try parsing JSON
          try {
            const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
            const jsonStr = jsonMatch ? jsonMatch[0] : cleanResponse;
            const parsed = JSON.parse(jsonStr);

            if (parsed.Status === 'INVALID') {
              validationStatus = 'error';
              validationMessage = parsed.Message || "Option is invalid.";
              suggestions = parsed.Suggestions || [];
            } else if (parsed.Status === 'OK') {
              validationStatus = 'success';
            } else {
              // Fallback for valid options returned as JSON
              validationStatus = 'success';
            }
          } catch (e) {
            console.warn("Failed to parse validation response:", cleanResponse);
            validationStatus = 'error';
            validationMessage = "Could not validate option.";
          }
        }

        setEditingBallot(prev => ({
          ...prev,
          options: prev.options.map(opt =>
            opt.id === optionId ? {
              ...opt,
              isValidating: false,
              validationStatus: validationStatus,
              validationMessage: validationMessage,
              suggestions: suggestions
            } : opt
          )
        }));

      } else {
        throw new Error("Failed to build validation prompt");
      }
    } catch (error) {
      console.error("Validation error:", error);
      setEditingBallot(prev => ({
        ...prev,
        options: prev.options.map(opt =>
          opt.id === optionId ? {
            ...opt,
            isValidating: false,
            validationStatus: 'error',
            validationMessage: "Validation failed."
          } : opt
        )
      }));
    }
  };

  const handleApplySuggestion = (optionId, suggestionText) => {
    setEditingBallot(prev => ({
      ...prev,
      options: prev.options.map(opt =>
        opt.id === optionId ? {
          ...opt,
          text: suggestionText,
          validationStatus: 'success',
          validationMessage: null,
          suggestions: []
        } : opt
      )
    }));
  };

  const handleRemoveOption = (id) => {
    setEditingBallot(prev => ({
      ...prev,
      options: prev.options.filter(opt => opt.id !== id)
    }));
  };

  const handleAddOption = () => {
    // Restrict manually added options to 1
    const manualOptionsCount = editingBallot.options.filter(opt => !opt.isOriginal).length;
    if (manualOptionsCount >= 1) {
      setSaveErrorMessage("You can only add 1 custom option manually.");
      return;
    }
    setSaveErrorMessage(""); // clear any previous error

    const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    const newId = editingBallot.options.length > 0
      ? Math.max(...editingBallot.options.map(o => o.id)) + 1
      : 1;
    const newLetter = letters[editingBallot.options.length] || String.fromCharCode(65 + editingBallot.options.length);

    setEditingBallot(prev => ({
      ...prev,
      options: [...prev.options, { id: newId, letter: newLetter, text: '', isOriginal: false }]
    }));
  };

  // Drag and drop handlers for reordering options
  const handleDragStart = (e, index) => {
    setDraggedOptionIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Add a slight delay to allow the drag image to be captured
    setTimeout(() => {
      e.target.closest('.ballot-option-edit-item')?.classList.add('dragging');
    }, 0);
  };

  const handleDragEnd = (e) => {
    setDraggedOptionIndex(null);
    e.target.closest('.ballot-option-edit-item')?.classList.remove('dragging');
    // Remove drag-over class from all items
    document.querySelectorAll('.ballot-option-edit-item').forEach(item => {
      item.classList.remove('drag-over');
    });
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // Add visual feedback
    const items = document.querySelectorAll('.ballot-option-edit-item');
    items.forEach((item, i) => {
      if (i === index && draggedOptionIndex !== index) {
        item.classList.add('drag-over');
      } else {
        item.classList.remove('drag-over');
      }
    });
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedOptionIndex === null || draggedOptionIndex === dropIndex) {
      return;
    }

    setEditingBallot(prev => {
      const newOptions = [...prev.options];
      // Remove the dragged item
      const [draggedItem] = newOptions.splice(draggedOptionIndex, 1);
      // Insert at the new position
      newOptions.splice(dropIndex, 0, draggedItem);

      // Update letters based on new order (A, B, C, D...)
      const updatedOptions = newOptions.map((opt, idx) => ({
        ...opt,
        letter: String.fromCharCode(65 + idx) // A=65, B=66, etc.
      }));

      return {
        ...prev,
        options: updatedOptions
      };
    });

    setDraggedOptionIndex(null);
    // Remove visual feedback
    document.querySelectorAll('.ballot-option-edit-item').forEach(item => {
      item.classList.remove('drag-over');
    });
  };

  const handleMediaChange = (e) => {
    // Sub-ballots cannot have media
    if (editingBallot && editingBallot.isSubBallot) {
      e.target.value = "";
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    // Determine media type
    if (file.type.startsWith('image/')) {
      setMediaType('image');
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else if (file.type.startsWith('video/')) {
      setMediaType('video');
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      // File type validation (PDF, Word, Excel)
      const validFileTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

      if (!validFileTypes.includes(file.type)) {
        showSnackbar('Only PDF, Word, and Excel documents are allowed.', 'error');
        e.target.value = ''; // Clear the input
        return;
      }

      setMediaType('file');
      setMediaPreview(null);
    }

    setMediaFile(file);
  };

  const handleRemoveMedia = () => {
    setMediaFile(null);
    setMediaType(null);
    setMediaPreview(null);
  };

  // ========== Location/Address Functions ==========

  // Initialize Google Maps services
  useEffect(() => {
    if (window.google && window.google.maps) {
      if (!autocompleteServiceRef.current) {
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      }
      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
    }
  }, [isOpen]);

  // Parse address components from Google Places result
  const parseAddressComponents = (components) => {
    const details = {
      city: '',
      state: '',
      country: '',
      zip: ''
    };

    components.forEach(component => {
      const types = component.types;
      if (types.includes('locality')) {
        details.city = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        details.state = component.long_name;
      } else if (types.includes('country')) {
        details.country = component.long_name;
      } else if (types.includes('postal_code')) {
        details.zip = component.long_name;
      }
    });

    return details;
  };

  // Reverse geocode coordinates to address
  const reverseGeocode = async (latitude, longitude) => {
    if (!geocoderRef.current) {
      if (window.google && window.google.maps) {
        geocoderRef.current = new window.google.maps.Geocoder();
      } else {
        return null;
      }
    }

    return new Promise((resolve, reject) => {
      geocoderRef.current.geocode(
        { location: { lat: latitude, lng: longitude } },
        (results, status) => {
          if (status === 'OK' && results[0]) {
            const addressComponents = results[0].address_components;
            const details = parseAddressComponents(addressComponents);
            const formattedAddress = results[0].formatted_address;
            resolve({ formattedAddress, details });
          } else {
            reject(new Error('Geocoding failed'));
          }
        }
      );
    });
  };

  // Handle auto-detect location
  const handleAutoDetectLocation = () => {
    if (!navigator.geolocation) {
      showSnackbar('Geolocation is not supported by your browser', 'error');
      return;
    }

    setIsDetectingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const result = await reverseGeocode(latitude, longitude);
          if (result) {
            setLocationDisplay(result.formattedAddress);
            setLocationDetails(result.details);
          }
        } catch (error) {
          console.error('Error reverse geocoding:', error);
          showSnackbar('Failed to detect your location. Please enter it manually.', 'error');
        } finally {
          setIsDetectingLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsDetectingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            showSnackbar('Location access denied. Please enable location permissions or enter your address manually.', 'warning');
            break;
          case error.POSITION_UNAVAILABLE:
            showSnackbar('Location information unavailable. Please enter your address manually.', 'warning');
            break;
          case error.TIMEOUT:
            showSnackbar('Location request timed out. Please try again or enter your address manually.', 'warning');
            break;
          default:
            showSnackbar('An error occurred while detecting your location. Please enter your address manually.', 'error');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Handle location input change with autocomplete
  const handleLocationInputChange = (e) => {
    const value = e.target.value;
    setLocationDisplay(value);

    if (!value.trim()) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    // Get autocomplete suggestions
    if (autocompleteServiceRef.current) {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: value,
          types: ['(regions)'] // Allow cities, states, provinces, and countries
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setLocationSuggestions(predictions);
            setShowLocationSuggestions(true);
          } else {
            setLocationSuggestions([]);
            setShowLocationSuggestions(false);
          }
        }
      );
    }
  };

  // Handle selecting a location suggestion
  const handleSelectLocationSuggestion = (suggestion) => {
    setLocationDisplay(suggestion.description);
    setShowLocationSuggestions(false);

    // Get place details for address components
    if (geocoderRef.current) {
      geocoderRef.current.geocode(
        { placeId: suggestion.place_id },
        (results, status) => {
          if (status === 'OK' && results[0]) {
            const details = parseAddressComponents(results[0].address_components);
            setLocationDetails(details);
          }
        }
      );
    }
  };

  // Clear location
  const handleClearLocation = () => {
    setLocationDisplay('');
    setLocationDetails({ city: '', state: '', country: '', zip: '' });
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationInputRef.current && !locationInputRef.current.contains(event.target)) {
        setShowLocationSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ========== End Location/Address Functions ==========

  const handleSaveBallot = async () => {
    setSaveErrorMessage(""); // Clear previous errors

    if (!editingBallot.title.trim()) {
      setSaveErrorMessage('Please enter a question title');
      return;
    }
    if (editingBallot.options.length < 2 || editingBallot.options.some(opt => !opt.text.trim())) {
      setSaveErrorMessage('Please provide at least 2 valid options');
      return;
    }

    // Check if manually added options are validated
    const unvalidatedOption = editingBallot.options.find(opt => !opt.isOriginal && opt.validationStatus !== 'success');
    if (unvalidatedOption) {
      setSaveErrorMessage('Please validate your custom option before saving.');
      return;
    }

    // Save ballot data locally (optional, e.g., for later reuse)
    const ballotData = {
      question: editingBallot.title.trim(),
      options: editingBallot.options.map(opt => opt.text.trim())
    };
    sessionStorage.setItem('ballotData', JSON.stringify(ballotData));

    // Also update the ballot inside the current chatbot message so that
    // the changes are immediately reflected in the UI.
    if (editingBallotId) {
      const match = editingBallotId.match(/^msg-(\d+)-ballot-(\d+)$/);
      if (match) {
        const messageIndex = parseInt(match[1], 10);
        const ballotIndex = parseInt(match[2], 10);

        setMessages(prevMessages =>
          prevMessages.map((msg, idx) => {
            if (idx !== messageIndex || !msg.parsed || !Array.isArray(msg.parsed.ballots)) {
              return msg;
            }

            const updatedBallots = msg.parsed.ballots.map((b, bi) => {
              if (bi !== ballotIndex) return b;
              return {
                title: editingBallot.title.trim(),
                options: editingBallot.options.map(opt => ({
                  letter: opt.letter,
                  text: opt.text.trim(),
                })),
                isSubBallot: !!editingBallot.isSubBallot,
                isSubBallot: !!editingBallot.isSubBallot,
                parentVoteId: editingBallot.parentVoteId || b.parentVoteId, // Preserve parentVoteId
                nestedOptionId: editingBallot.nestedOptionId || b.nestedOptionId, // Preserve nestedOptionId
              };
            });

            const savedBallot = updatedBallots[ballotIndex];

            // For sub-ballots, keep all ballots visible (so user can save them one by one)
            // For main ballots, keep only the saved ballot
            const ballotsToKeep = editingBallot.isSubBallot ? updatedBallots : (savedBallot ? [savedBallot] : []);

            return {
              ...msg,
              parsed: {
                ...msg.parsed,
                ballots: ballotsToKeep,
              },
            };
          })
        );
      }
    }

    // Call backend API to actually create the vote from this ballot
    try {
      const userId = getUserId();
      if (!userId) {
        console.warn("User ID not found; cannot create vote user");
      } else {
        const formData = new FormData();
        // Required / important fields
        formData.append("UserId", userId);

        // If this is a sub-ballot, include the ParentId
        if (editingBallot.isSubBallot && editingBallot.parentVoteId) {
          formData.append("ParentId", String(editingBallot.parentVoteId));
        }

        formData.append("Title", editingBallot.title.trim());
        formData.append("IsActive", "true");
        formData.append("OptionType", "MCQ");
        formData.append("Priority", "1");

        // Use explicit "false" instead of "" for booleans to avoid binding errors
        formData.append("IsFaceRequired", "false");
        formData.append("IsPaused", "false");

        // Only append optional strings if they have content
        if (locationDetails.city) formData.append("City", locationDetails.city);
        if (locationDetails.zip) formData.append("Zipcode", String(locationDetails.zip));
        if (locationDetails.state) formData.append("State", locationDetails.state);
        if (locationDetails.country) formData.append("Country", locationDetails.country);
        if (editingBallot.description) formData.append("Description", editingBallot.description);

        // Empty date fields often cause issues if sent as ""; better to omit or send null
        // formData.append("EndDate", ""); 
        // formData.append("StartDate", "");

        // Add media file based on type (only one can be uploaded)
        if (mediaFile) {
          if (mediaType === 'image') {
            formData.append("Images", mediaFile);
          } else if (mediaType === 'video') {
            formData.append("Video", mediaFile);
          } else if (mediaType === 'file') {
            formData.append("File", mediaFile);
            formData.append("FileName", mediaFile.name);
          }
        }


        // Sub-ballots need NestedOptionId
        if (editingBallot.isSubBallot && editingBallot.nestedOptionId) {
          formData.append("NestedOptionId", editingBallot.nestedOptionId);
        }

        // Options (multiple entries)
        editingBallot.options.forEach(opt => {
          const value = opt.text.trim();
          if (value) {
            formData.append("Options", value);
          }
        });

        // Add historyId and BallotId if available
        if (editingBallotId) {
          const match = editingBallotId.match(/^msg-(\d+)-ballot-(\d+)$/);
          if (match) {
            const msgIndex = parseInt(match[1], 10);
            const msg = messages[msgIndex];

            if (msg && msg.historyId) {
              formData.append("ChatHistoryId", String(msg.historyId));
            } else {
              console.warn("⚠️ ChatHistoryId not yet available for this message");
            }

            const bIndex = parseInt(match[2], 10);
            if (msg && msg.parsed && msg.parsed.ballots && msg.parsed.ballots[bIndex]) {
              const ballot = msg.parsed.ballots[bIndex];
              if (ballot && ballot.id) {
                formData.append("BallotId", String(ballot.id));
              }
            }
          }
        }

        const response = await multipartVoteApi.post(
          "/AIBallotsSaving/create-vote-user",
          formData
        );
        console.log("Created vote user from AI ballot:", response.data);

        // Mark this ballot as saved
        if (editingBallotId) {
          setSavedBallotIds(prev => new Set([...prev, editingBallotId]));
        }

        if (onBallotCreated) {
          console.log("Calling onBallotCreated from Chatbot (main ballot)");
          onBallotCreated();
        }

        // Remember parent vote and ballot for potential sub-ballots (only for main ballots, not sub-ballots)
        if (!editingBallot.isSubBallot) {
          const savedVoteId = response?.data?.voteId || response?.data?.data?.voteId || null;
          // Try different casing or locations for options
          const savedOptions = response?.data?.options || response?.data?.data?.options || response?.data?.data?.Options || [];

          console.log("Saved Parent Vote ID:", savedVoteId);
          console.log("Saved Parent Options:", savedOptions);

          if (savedVoteId) {
            setParentVoteId(savedVoteId);
            setParentVoteOptions(savedOptions); // Store options to get IDs for sub-ballots
            setShowSubBallotPrompt(true);
          }
        }
      }
    } catch (error) {
      console.error("Error creating vote user from AI ballot:", error);
    }

    // Reset editing state
    setEditingBallotId(null);
    setEditingBallot(null);
    setMediaFile(null);
    setMediaType(null);
    setMediaPreview(null);
    // Reset location state
    setLocationDisplay('');
    setLocationDetails({ city: '', state: '', country: '', zip: '' });
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);
  };

  const handleCancelEdit = () => {
    setEditingBallotId(null);
    setEditingBallot(null);
    setMediaFile(null);
    setMediaType(null);
    setMediaPreview(null);
    // Reset location state
    setLocationDisplay('');
    setLocationDetails({ city: '', state: '', country: '', zip: '' });
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);
  };

  const handleSubBallotNo = () => {
    setShowSubBallotPrompt(false);
    // Show final confirmation message
    setMessages(prev => [
      ...prev,
      {
        role: "assistant",
        content: "This is your final ballot.",
      },
    ]);
  };

  // ========== Bulk Sub-Ballot Management Functions ==========

  // Initialize editable sub-ballots from the generated sub-ballots
  const initializeEditableSubBallots = (subBallots) => {
    const editable = subBallots.map((ballot, idx) => ({
      id: idx,
      title: ballot.title,
      parentVoteId: ballot.parentVoteId,
      nestedOptionId: ballot.nestedOptionId,
      options: ballot.options.map((opt, optIdx) => ({
        id: optIdx + 1,
        letter: opt.letter,
        text: opt.text,
        isOriginal: true // Mark AI-generated options as original (non-editable)
      }))
    }));
    setEditableSubBallots(editable);
  };

  // Toggle edit mode for all sub-ballots
  const handleToggleEditSubBallots = (subBallots) => {
    if (!isEditingSubBallots) {
      // Entering edit mode - initialize editable copies
      initializeEditableSubBallots(subBallots);
    }
    setIsEditingSubBallots(!isEditingSubBallots);
  };

  // Cancel sub-ballot editing
  const handleCancelSubBallotEdit = () => {
    setIsEditingSubBallots(false);
    setEditableSubBallots([]);
  };

  // Edit a specific sub-ballot's title
  const handleEditSubBallotTitle = (subBallotId, value) => {
    setEditableSubBallots(prev =>
      prev.map(sb =>
        sb.id === subBallotId ? { ...sb, title: value } : sb
      )
    );
  };

  // Edit a specific option in a sub-ballot
  const handleEditSubBallotOption = (subBallotId, optionId, value) => {
    setEditableSubBallots(prev =>
      prev.map(sb =>
        sb.id === subBallotId
          ? {
            ...sb,
            options: sb.options.map(opt =>
              opt.id === optionId ? { ...opt, text: value } : opt
            )
          }
          : sb
      )
    );
  };

  // Remove option from a sub-ballot
  const handleRemoveSubBallotOption = (subBallotId, optionId) => {
    setEditableSubBallots(prev =>
      prev.map(sb =>
        sb.id === subBallotId
          ? { ...sb, options: sb.options.filter(opt => opt.id !== optionId) }
          : sb
      )
    );
  };

  // Add option to a sub-ballot
  const handleAddSubBallotOption = (subBallotId) => {
    setEditableSubBallots(prev =>
      prev.map(sb => {
        if (sb.id !== subBallotId) return sb;
        const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
        const newId = sb.options.length > 0
          ? Math.max(...sb.options.map(o => o.id)) + 1
          : 1;
        const newLetter = letters[sb.options.length] || String.fromCharCode(65 + sb.options.length);
        return {
          ...sb,
          options: [...sb.options, { id: newId, letter: newLetter, text: '', isOriginal: false }]
        };
      })
    );
  };

  // Drag and drop state for sub-ballots
  const [draggedSubBallotOption, setDraggedSubBallotOption] = useState({ subBallotId: null, optionIndex: null });

  // Drag and drop handlers for sub-ballot options
  const handleSubBallotDragStart = (e, subBallotId, optionIndex) => {
    setDraggedSubBallotOption({ subBallotId, optionIndex });
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => {
      e.target.closest('.sub-ballot-option-edit-item')?.classList.add('dragging');
    }, 0);
  };

  const handleSubBallotDragEnd = (e) => {
    setDraggedSubBallotOption({ subBallotId: null, optionIndex: null });
    e.target.closest('.sub-ballot-option-edit-item')?.classList.remove('dragging');
    document.querySelectorAll('.sub-ballot-option-edit-item').forEach(item => {
      item.classList.remove('drag-over');
    });
  };

  const handleSubBallotDragOver = (e, subBallotId, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // Only allow drag within the same sub-ballot
    if (draggedSubBallotOption.subBallotId !== subBallotId) return;

    const items = e.target.closest('.sub-ballot-options-edit')?.querySelectorAll('.sub-ballot-option-edit-item');
    items?.forEach((item, i) => {
      if (i === index && draggedSubBallotOption.optionIndex !== index) {
        item.classList.add('drag-over');
      } else {
        item.classList.remove('drag-over');
      }
    });
  };

  const handleSubBallotDrop = (e, subBallotId, dropIndex) => {
    e.preventDefault();

    const { subBallotId: dragSubBallotId, optionIndex: dragIndex } = draggedSubBallotOption;

    // Only allow drop within same sub-ballot
    if (dragSubBallotId !== subBallotId || dragIndex === null || dragIndex === dropIndex) {
      return;
    }

    setEditableSubBallots(prev =>
      prev.map(sb => {
        if (sb.id !== subBallotId) return sb;

        const newOptions = [...sb.options];
        const [draggedItem] = newOptions.splice(dragIndex, 1);
        newOptions.splice(dropIndex, 0, draggedItem);

        // Update letters based on new order
        const updatedOptions = newOptions.map((opt, idx) => ({
          ...opt,
          letter: String.fromCharCode(65 + idx)
        }));

        return { ...sb, options: updatedOptions };
      })
    );

    setDraggedSubBallotOption({ subBallotId: null, optionIndex: null });
    document.querySelectorAll('.sub-ballot-option-edit-item').forEach(item => {
      item.classList.remove('drag-over');
    });
  };

  const handleSaveAllSubBallots = async (ballotsToSave) => {
    // Use provided ballots or fallback to state
    const targetBallots = ballotsToSave || editableSubBallots;

    if (!targetBallots || targetBallots.length === 0) {
      console.warn("No sub-ballots to save");
      return;
    }

    // Validate all sub-ballots
    for (const sb of targetBallots) {
      if (!sb.title || !sb.title.trim()) {
        showSnackbar(`Please enter a title for sub-ballot ${sb.id + 1}`, 'error');
        return;
      }
      if (!sb.options || sb.options.length < 2 || sb.options.some(opt => !opt.text || !opt.text.trim())) {
        showSnackbar(`Please provide at least 2 valid options for sub-ballot "${sb.title}"`, 'error');
        return;
      }
    }

    try {
      setIsSavingSubBallots(true);
      const userId = getUserId();
      if (!userId) {
        console.warn("User ID not found; cannot save sub-ballots");
        showSnackbar("User not logged in. Please log in to save sub-ballots.", 'error');
        return;
      }

      // Prepare the payload for bulk API
      const payload = targetBallots.map(sb => ({
        userId: userId,
        title: sb.title.trim(),
        isActive: true,
        parentId: sb.parentVoteId,
        nestedOptionId: sb.nestedOptionId,
        options: sb.options.map(opt => opt.text.trim()).filter(Boolean)
      }));

      console.log("Saving sub-ballots with payload:", payload);

      // Call the bulk create API
      const response = await voteApi.post(
        "/AIBallotsSaving/create-sub-ballots",
        payload
      );

      console.log("Sub-ballots saved successfully:", response.data);

      if (response.data?.success) {
        // Mark sub-ballots as saved
        setSubBallotsSaved(true);
        setIsEditingSubBallots(false);

        // Update the messages to reflect saved state
        setMessages(prev => [
          ...prev,
          {
            role: "assistant",
            content: "All sub-ballots have been saved successfully!",
          },
        ]);

        if (onBallotCreated) {
          console.log("Calling onBallotCreated from Chatbot (sub-ballots)");
          onBallotCreated();
        }
      } else {
        showSnackbar("Failed to save sub-ballots. Please try again.", 'error');
      }
    } catch (error) {
      console.error("Error saving sub-ballots:", error);
      showSnackbar("An error occurred while saving sub-ballots. Please try again.", 'error');
    } finally {
      setIsSavingSubBallots(false);
    }
  };

  // ========== End Bulk Sub-Ballot Management Functions ==========

  const handleSubBallotYes = async () => {
    if (!parentVoteId || isGeneratingSubBallots) return;

    try {
      setIsGeneratingSubBallots(true);
      const userId = getUserId();
      if (!userId) {
        console.warn("User ID not found; cannot create sub-ballots");
        return;
      }

      // Find the latest assistant message that has a ballot (the saved parent ballot)
      let parentBallot = null;
      for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (msg.parsed && Array.isArray(msg.parsed.ballots) && msg.parsed.ballots.length > 0) {
          parentBallot = msg.parsed.ballots[0];
          break;
        }
      }

      if (!parentBallot || !parentBallot.options || parentBallot.options.length === 0) {
        console.warn("Parent ballot or options not found; cannot generate sub-ballots");
        return;
      }

      const optionTexts = parentBallot.options
        .map(opt => (typeof opt.text === "string" ? opt.text.trim() : ""))
        .filter(Boolean);

      if (optionTexts.length === 0) {
        console.warn("No valid option texts for sub-ballots");
        return;
      }

      // For each option, ask Azure AI to generate a dedicated sub-ballot
      const generatedSubBallots = [];

      for (const optionText of optionTexts) {
        try {
          // 1. Get prompt from backend for THIS specific option
          // We pass [optionText] assuming we want a specific prompt for this single option
          const promptResponse = await preparePromptSubBallot(parentBallot.title, [optionText]);

          let aiMessages = [];
          if (promptResponse && promptResponse.success && Array.isArray(promptResponse.data)) {
            aiMessages = promptResponse.data;
          } else {
            throw new Error("Failed to prepare prompt for sub-ballot");
          }

          // 2. Call Azure AI directly from frontend
          if (!azureCredentials) {
            console.warn("Missing Azure credentials, attempting fetch...");
            await fetchAzureCredentials();
            if (!azureCredentials) throw new Error("Could not fetch Azure credentials");
          }

          const aiResponseText = await callAzureOpenAI(azureCredentials, aiMessages);

          // 3. Parse response
          const parsed = parseBallotResponse(aiResponseText);
          let subBallot =
            parsed && parsed.ballots && parsed.ballots.length > 0
              ? parsed.ballots[0]
              : null;

          // Fallback if AI output can't be parsed into a valid ballot
          if (!subBallot || !subBallot.options || subBallot.options.length < 2) {
            console.warn(
              "AI did not return a valid sub-ballot for option, using fallback:",
              optionText
            );
            subBallot = {
              title: `What would you like to do regarding "${optionText}"?`,
              options: [
                { letter: "A", text: `Know more about ${optionText}` },
                { letter: "B", text: `Compare ${optionText} with other options` },
                { letter: "C", text: `See pros and cons of ${optionText}` },
                { letter: "D", text: "Skip for now" },
              ],
            };
          }

          // Sub-ballot generated - will be saved only when user clicks "Use This" and "Save"
          generatedSubBallots.push({
            ...subBallot,
            isSubBallot: true,
            parentVoteId: parentVoteId, // Store parent ID for later when user saves
          });
        } catch (err) {
          console.error("Error generating sub-ballot for option:", optionText, err);
        }
      }

      // Map locally generated sub-ballots to our internal structure
      // We need to map them back to the parent options to get the NestedOptionId.
      const subBallotsForUi = generatedSubBallots.map((subBallot, index) => {
        // Find the corresponding parent option ID
        // We assume generatedSubBallots are in the same order as optionTexts.
        let nestedOptionId = null;
        if (parentVoteOptions && parentVoteOptions.length > index) {
          // Assuming parentVoteOptions are in the same order as the ballot options
          nestedOptionId = parentVoteOptions[index]?.id || parentVoteOptions[index]?.Id;
        }

        console.log(`Sub-ballot for option ${index}: mapped to NestedOptionId ${nestedOptionId}`);

        return {
          ...subBallot,
          nestedOptionId: nestedOptionId
        };
      });

      if (subBallotsForUi.length === 0) {
        console.warn("No sub-ballots were generated.");
      }

      const placeholderParts = subBallotsForUi.map((_, idx) => `__BALLOT_${idx}__`);
      const subBallotsText =
        ["Here are the sub-ballots generated for each option:", ...placeholderParts].join(
          "\n"
        );

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Here are the sub-ballots generated for each option:",
          parsed: {
            text: subBallotsText,
            ballots: subBallotsForUi,
            hasBallots: true,
          },
          fromHistory: false
        },
      ]);
    } catch (error) {
      console.error("Error creating sub-ballots:", error);
    } finally {
      setIsGeneratingSubBallots(false);
      setShowSubBallotPrompt(false);
    }
  };

  const formatMessage = (text, ballots, messageIndex, canShowButtons) => {
    console.log(`📋 formatMessage called: msgIndex=${messageIndex}, text="${text?.substring(0, 50)}", ballots=`, ballots);

    if (!ballots || ballots.length === 0) {
      if (!text) return null;
      const lines = text.split('\n').filter(line => line.trim());
      return (
        <>
          {lines.map((line, idx) => (
            <p key={idx}>{line.trim()}</p>
          ))}
        </>
      );
    }

    // Check if this message contains sub-ballots
    const hasSubBallots = ballots.some(b => !!b.isSubBallot);

    if (hasSubBallots) {
      const subBallots = ballots.filter(b => !!b.isSubBallot);
      const introText = (text || "").split('__BALLOT_')[0].trim();

      return (
        <>
          {introText && <p>{introText}</p>}
          <div className={`sub-ballots-unified-container ${subBallotsSaved ? 'saved' : ''}`}>
            <div className="sub-ballots-header">
              <span className="sub-ballots-title">Sub-Ballots ({subBallots.length})</span>
              {!subBallotsSaved && (
                <button
                  className="sub-ballots-edit-btn"
                  onClick={() => handleToggleEditSubBallots(subBallots)}
                  title={isEditingSubBallots ? "Cancel Edit" : "Edit All"}
                  aria-label={isEditingSubBallots ? "Cancel Edit" : "Edit All Sub-Ballots"}
                >
                  {isEditingSubBallots ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  )}
                </button>
              )}
              {subBallotsSaved && <span className="sub-ballots-saved-badge">✓ Saved</span>}
            </div>

            <div className="sub-ballots-list">
              {isEditingSubBallots ? (
                editableSubBallots.map((sb, sbIndex) => (
                  <div key={sb.id} className="sub-ballot-edit-card">
                    <div className="sub-ballot-edit-header">
                      <span className="sub-ballot-number">Sub-Ballot {sbIndex + 1}</span>
                    </div>

                    <div className="sub-ballot-edit-section">
                      <label className="sub-ballot-edit-label">Question Title</label>
                      <input
                        type="text"
                        className="sub-ballot-edit-input"
                        value={sb.title}
                        readOnly // Make sub-ballot title read-only
                        placeholder="Enter sub-ballot question..."
                      />
                    </div>

                    <div className="sub-ballot-edit-section">
                      <label className="sub-ballot-edit-label">Options</label>
                      <div className="sub-ballot-options-edit">
                        {sb.options.map((option, optionIndex) => (
                          <div
                            key={option.id}
                            className={`sub-ballot-option-edit-item ${draggedSubBallotOption.subBallotId === sb.id && draggedSubBallotOption.optionIndex === optionIndex ? 'dragging' : ''}`}
                            draggable
                            onDragStart={(e) => handleSubBallotDragStart(e, sb.id, optionIndex)}
                            onDragEnd={handleSubBallotDragEnd}
                            onDragOver={(e) => handleSubBallotDragOver(e, sb.id, optionIndex)}
                            onDrop={(e) => handleSubBallotDrop(e, sb.id, optionIndex)}
                          >
                            <button
                              className="remove-option-btn"
                              onClick={() => handleRemoveSubBallotOption(sb.id, option.id)}
                              aria-label="Remove option"
                            >
                              ×
                            </button>
                            <span className="option-letter">{option.letter}.</span>
                            <input
                              type="text"
                              className="sub-ballot-option-edit-input"
                              value={option.text}
                              readOnly={option.isOriginal}
                              onChange={option.isOriginal ? undefined : (e) => handleEditSubBallotOption(sb.id, option.id, e.target.value)}
                              placeholder={`Option ${option.letter}`}
                            />
                            <div className="drag-handle" title="Drag to reorder">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <circle cx="9" cy="6" r="1.5" />
                                <circle cx="15" cy="6" r="1.5" />
                                <circle cx="9" cy="12" r="1.5" />
                                <circle cx="15" cy="12" r="1.5" />
                                <circle cx="9" cy="18" r="1.5" />
                                <circle cx="15" cy="18" r="1.5" />
                              </svg>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        className="add-option-btn small"
                        onClick={() => handleAddSubBallotOption(sb.id)}
                      >
                        + Add Option
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                subBallots.map((ballot, sbIndex) => (
                  <div key={sbIndex} className={`sub-ballot-card ${subBallotsSaved ? 'saved' : ''}`}>
                    <div className="sub-ballot-card-header">
                      <span className="sub-ballot-number">Sub-Ballot {sbIndex + 1}</span>
                    </div>
                    <div className="sub-ballot-card-title">{ballot.title}</div>
                    <div className="sub-ballot-card-options">
                      {ballot.options.map((option, optIndex) => (
                        <div key={optIndex} className="sub-ballot-card-option">
                          {option.letter}. {option.text}
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer with Save All Button */}
            {!subBallotsSaved && canShowButtons && (
              <div className="sub-ballots-footer">
                {isEditingSubBallots && (
                  <button
                    className="sub-ballots-cancel-btn"
                    onClick={handleCancelSubBallotEdit}
                  >
                    Cancel
                  </button>
                )}
                <button
                  className="sub-ballots-save-btn"
                  onClick={() => {
                    if (isEditingSubBallots) {
                      handleSaveAllSubBallots();
                    } else {
                      const ballotsToSave = subBallots.map((ballot, idx) => ({
                        id: idx,
                        title: ballot.title,
                        parentVoteId: ballot.parentVoteId,
                        nestedOptionId: ballot.nestedOptionId,
                        options: ballot.options.map((opt, optIdx) => ({
                          id: optIdx + 1,
                          letter: opt.letter,
                          text: opt.text
                        }))
                      }));
                      handleSaveAllSubBallots(ballotsToSave);
                    }
                  }}
                  disabled={isSavingSubBallots}
                >
                  {isSavingSubBallots ? 'Saving...' : 'Save All Sub-Ballots'}
                </button>
              </div>
            )}
          </div>
        </>
      );
    }

    // Original logic for main ballots (non-sub-ballots)
    const normalizedText = text || "";
    const parts = normalizedText.split(/(__BALLOT_\d+__)/);
    const renderedIndices = new Set();

    return (
      <>
        {parts.map((part, index) => {
          if (part.startsWith('__BALLOT_')) {
            const m = part.match(/\d+/);
            if (m) {
              const ballotIndex = parseInt(m[0], 10);
              renderedIndices.add(ballotIndex);
              const ballot = ballots[ballotIndex];
              if (ballot && !ballot.isSubBallot) {
                const ballotId = `msg-${messageIndex}-ballot-${ballotIndex}`;
                const isSaved = savedBallotIds.has(ballotId);
                const isSelected = selectedBallotId === ballotId;

                // Show editable form if this ballot is being edited
                if (editingBallotId === ballotId && editingBallot) {
                  return (
                    <div key={index} className="ballot-container editing">
                      <div className="ballot-edit-section">
                        <label className="ballot-edit-label">Question Title</label>
                        <input
                          type="text"
                          className="ballot-edit-input"
                          value={editingBallot.title}
                          readOnly
                          placeholder="Enter your question..."
                        />
                      </div>

                      <div className="ballot-edit-section">
                        <label className="ballot-edit-label">Answer Options</label>
                        <div className="ballot-options-edit">
                          {editingBallot.options.map((option, optionIndex) => (
                            <div
                              key={option.id}
                              className={`ballot-option-edit-item ${draggedOptionIndex === optionIndex ? 'dragging' : ''}`}
                              draggable
                              onDragStart={(e) => handleDragStart(e, optionIndex)}
                              onDragEnd={handleDragEnd}
                              onDragOver={(e) => handleDragOver(e, optionIndex)}
                              onDrop={(e) => handleDrop(e, optionIndex)}
                            >
                              <button
                                className="remove-option-btn"
                                onClick={() => handleRemoveOption(option.id)}
                                aria-label="Remove option"
                              >
                                ×
                              </button>
                              <span className="option-letter">{option.letter}.</span>
                              <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
                                <input
                                  type="text"
                                  className="ballot-option-edit-input"
                                  value={option.text}
                                  readOnly={option.isOriginal}
                                  onChange={option.isOriginal ? undefined : (e) => handleEditOption(option.id, e.target.value)}
                                  placeholder={`Option ${option.letter}`}
                                  style={{ paddingRight: option.isOriginal ? '14px' : '40px', width: '100%' }} // Make room for icon
                                />
                                {/* Validation Status (Automatic, non-clickable) */}
                                {!option.isOriginal && (
                                  <div style={{ position: 'absolute', right: '8px', zIndex: 2, display: 'flex', alignItems: 'center' }}>
                                    <div className={`validate-option-status ${option.isValidating ? 'loading' : ''} ${option.validationStatus === 'success' ? 'success' : option.validationStatus === 'error' ? 'error' : ''}`}>
                                      {option.isValidating ? (
                                        <FiLoader className="spin" />
                                      ) : option.validationStatus === 'success' ? (
                                        <FiCheck style={{ color: '#4caf50' }} />
                                      ) : option.validationStatus === 'error' ? (
                                        <FiAlertCircle style={{ color: '#f44336' }} />
                                      ) : null}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="drag-handle" title="Drag to reorder">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <circle cx="9" cy="6" r="1.5" />
                                  <circle cx="15" cy="6" r="1.5" />
                                  <circle cx="9" cy="12" r="1.5" />
                                  <circle cx="15" cy="12" r="1.5" />
                                  <circle cx="9" cy="18" r="1.5" />
                                  <circle cx="15" cy="18" r="1.5" />
                                </svg>
                              </div>

                              {/* Detailed Validation Feedback (Below the row) */}
                              {option.validationStatus === 'error' && (
                                <div className="option-validation-error">
                                  <span className="error-text">{option.validationMessage}</span>
                                  {option.suggestions && option.suggestions.length > 0 && (
                                    <div className="option-suggestions">
                                      {option.suggestions.map((suggestion, sIdx) => (
                                        <div
                                          key={sIdx}
                                          className="option-suggestion-pill"
                                          onClick={() => handleApplySuggestion(option.id, suggestion)}
                                        >
                                          {suggestion}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        {editingBallot.options.filter(o => !o.isOriginal).length < 1 ? (
                          <button className="add-option-btn" onClick={handleAddOption}>
                            + Add Option
                          </button>
                        ) : (
                          <p style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>
                            Maximum 1 custom option allowed.
                          </p>
                        )}
                      </div>

                      {/* Location Section (parent ballots only) */}
                      {
                        !editingBallot.isSubBallot && (
                          <div className="ballot-edit-section">
                            <div className="ballot-edit-label-row">
                              <label className="ballot-edit-label">Location (Optional)</label>
                              <button
                                type="button"
                                className="auto-detect-btn"
                                onClick={handleAutoDetectLocation}
                                disabled={isDetectingLocation}
                              >
                                {isDetectingLocation ? (
                                  <>
                                    <span className="detecting-spinner"></span>
                                    Detecting...
                                  </>
                                ) : (
                                  <>
                                    <svg className="gps-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <circle cx="12" cy="12" r="3" />
                                      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
                                    </svg>
                                    Auto-detect
                                  </>
                                )}
                              </button>
                            </div>
                            <div className="location-input-wrapper" ref={locationInputRef}>
                              <input
                                type="text"
                                className="ballot-edit-input location-input"
                                value={locationDisplay}
                                onChange={handleLocationInputChange}
                                placeholder="Enter city, state, or address..."
                              />
                              {locationDisplay && (
                                <button
                                  type="button"
                                  className="location-clear-btn"
                                  onClick={handleClearLocation}
                                  aria-label="Remove media"
                                >
                                  ×
                                </button>
                              )}
                              {showLocationSuggestions && locationSuggestions.length > 0 && (
                                <ul className="location-suggestions">
                                  {locationSuggestions.map((suggestion) => (
                                    <li
                                      key={suggestion.place_id}
                                      className="location-suggestion-item"
                                      onClick={() => handleSelectLocationSuggestion(suggestion)}
                                    >
                                      <svg className="location-pin-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                                      </svg>
                                      <span>{suggestion.description}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                            {locationDetails.city && (
                              <div className="location-parsed-details">
                                <span className="location-detail-chip">
                                  {locationDetails.city}
                                  {locationDetails.state && `, ${locationDetails.state}`}
                                </span>
                                {locationDetails.country && (
                                  <span className="location-detail-chip">{locationDetails.country}</span>
                                )}
                                {locationDetails.zip && (
                                  <span className="location-detail-chip">ZIP: {locationDetails.zip}</span>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      }

                      {/* Media Upload Section (parent ballots only) */}
                      {
                        !editingBallot.isSubBallot && (
                          <div className="ballot-edit-section">
                            <label className="ballot-edit-label">Add Media (Optional)</label>
                            <div className="ballot-media-upload">
                              {mediaPreview ? (
                                <div className="ballot-media-preview">
                                  {mediaType === 'image' && (
                                    <img src={mediaPreview} alt="Preview" />
                                  )}
                                  {mediaType === 'video' && (
                                    <video src={mediaPreview} controls />
                                  )}
                                  {mediaType === 'file' && (
                                    <div className="file-preview">
                                      <div className="file-icon">📄</div>
                                      <div className="file-name">{mediaFile?.name}</div>
                                    </div>
                                  )}
                                  <button
                                    className="remove-media-btn"
                                    onClick={handleRemoveMedia}
                                    aria-label="Remove media"
                                  >
                                    ×
                                  </button>
                                </div>
                              ) : (
                                <div className="ballot-media-placeholder">
                                  <div className="media-icon">📷</div>
                                  <p>Upload an image, video, or file</p>
                                  <p className="media-hint">(PDF, Word, Excel only for files)</p>
                                  <input
                                    type="file"
                                    id="ballot-media-upload"
                                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                                    onChange={handleMediaChange}
                                    style={{ display: 'none' }}
                                  />
                                  <label htmlFor="ballot-media-upload" className="choose-media-btn">
                                    Choose File
                                  </label>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      }

                      <div className="ballot-edit-actions-container">
                        {saveErrorMessage && (
                          <div className="save-error-message" style={{ color: '#ff5252', marginBottom: '8px', fontSize: '13px', textAlign: 'center' }}>
                            {saveErrorMessage}
                          </div>
                        )}
                        <div className="ballot-edit-actions">
                          <button className="cancel-edit-btn" onClick={handleCancelEdit}>
                            Cancel
                          </button>
                          <button className="save-ballot-btn" onClick={handleSaveBallot}>
                            Save
                          </button>
                        </div>
                      </div>
                    </div >
                  );
                }

                return (
                  <div key={index} className={`ballot-container ${isSelected ? 'selected' : ''} ${isSaved && !ballot.isExisting ? 'saved' : ''} ${ballot.isExisting ? 'existing' : ''}`}>
                    {isSaved && !ballot.isExisting && (
                      <div className="ballot-saved-badge-overlay">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        Saved
                      </div>
                    )}
                    {ballot.isExisting && (
                      <div className="ballot-saved-badge-overlay" style={{ background: "#2196f3", color: "#fff", display: "flex", alignItems: "center", gap: "4px" }}>
                        🔗 Active Ballot
                      </div>
                    )}
                    <div className="ballot-title">{ballot.title}</div>
                    <div className="ballot-options">
                      {ballot.options.map((option, optIdx) => (
                        <div key={optIdx} className="ballot-option">
                          {option.letter}. {option.text}
                        </div>
                      ))}
                    </div>
                    {ballot.isExisting ? (
                      <button
                        className="ballot-keep-btn selected"
                        onClick={() => {
                          setIsOpen(false);
                          navigate(`/vote?voteId=${ballot.voteId}`);
                        }}
                      >
                        Open Ballot
                      </button>
                    ) : (
                      canShowButtons && (
                        <button
                          className={`ballot-keep-btn ${isSelected ? 'selected' : ''} ${isSaved ? 'saved' : ''}`}
                          onClick={() => handleKeepBallot(ballotId, ballot)}
                          disabled={isSaved}
                        >
                          {isSaved ? 'Saved' : 'Use This'}
                        </button>
                      )
                    )}
                  </div>
                );
              }
            }
            return null;
          }

          if (part.trim()) {
            const lines = part.split('\n').filter(line => line.trim());
            return lines.map((line, idx) => (
              <p key={`${index}-${idx}`}>{line.trim()}</p>
            ));
          }
          return null;
        })}

        {/* --- Render Orphan Ballots (those not found in text) --- */}
        {
          ballots
            .map((b, i) => ({ ...b, originalIndex: i }))
            .filter(b => !renderedIndices.has(b.originalIndex) && !b.isSubBallot)
            .map((ballot) => {
              const bIndex = ballot.originalIndex;
              const ballotId = `msg-${messageIndex}-ballot-${bIndex}`;
              const isSaved = savedBallotIds.has(ballotId);
              const isSelected = selectedBallotId === ballotId;

              return (
                <div key={`orphan-${bIndex}`} className={`ballot-container ${isSelected ? 'selected' : ''} ${isSaved && !ballot.isExisting ? 'saved' : ''} ${ballot.isExisting ? 'existing' : ''}`}>
                  {isSaved && !ballot.isExisting && (
                    <div className="ballot-saved-badge-overlay">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Saved
                    </div>
                  )}
                  {ballot.isExisting && (
                    <div className="ballot-saved-badge-overlay" style={{ background: "#2196f3", color: "#fff", display: "flex", alignItems: "center", gap: "4px" }}>
                      🔗 Active Ballot
                    </div>
                  )}
                  <div className="ballot-title">{ballot.title}</div>
                  <div className="ballot-options">
                    {ballot.options.map((option, optIndex) => (
                      <div key={optIndex} className="ballot-option">
                        {option.letter}. {option.text}
                      </div>
                    ))}
                  </div>
                  {ballot.isExisting ? (
                    <button
                      className="ballot-keep-btn selected"
                      onClick={() => {
                        setIsOpen(false);
                        navigate(`/vote?voteId=${ballot.voteId}`);
                      }}
                    >
                      Open Ballot
                    </button>
                  ) : (
                    canShowButtons && (
                      <button
                        className="ballot-keep-btn"
                        onClick={() => handleKeepBallot(ballotId, ballot)}
                        disabled={isSaved}
                      >
                        {isSaved ? 'Saved' : 'Use This'}
                      </button>
                    )
                  )}
                </div>
              );
            })
        }
      </>
    );
  };

  const handleSend = async (overrideText) => {
    // Support being called from onClick (which passes an event) or with a string override
    const raw =
      typeof overrideText === "string"
        ? overrideText
        : typeof inputValue === "string"
          ? inputValue
          : "";

    const trimmed = raw.trim();
    if (!trimmed || isLoading) return;

    const userMessage = trimmed;

    // Clear input only when sending from the input box
    if (typeof overrideText !== "string") {
      setInputValue("");
    }

    // Add user message to UI immediately
    const newMessages = [
      ...messages,
      { role: "user", content: userMessage },
    ];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const userId = getUserId();

      // Check if a ballot matching the user's prompt already exists in the system
      try {
        const token = getToken();
        if (token && userId) {
          const existingVotes = await getAllVotesWithFilters({
            userId: userId,
            skip: 0,
            take: 100
          }, token);

          if (existingVotes && Array.isArray(existingVotes)) {
            // Find if any existing vote's title matches the query
            const query = userMessage.toLowerCase().trim();
            const match = existingVotes.find(v => {
              const title = v.title.toLowerCase();
              return title.includes(query) || query.includes(title);
            });

            if (match) {
              // Found existing ballot! Render it inline and bypass Azure OpenAI call
              const matchedBallot = {
                id: `existing-ballot-${match.voteId}`,
                title: match.title,
                question: match.title,
                options: match.options.map((opt, optIndex) => ({
                  letter: opt.letter || String.fromCharCode(65 + optIndex), // A, B, C, etc.
                  text: opt.value
                })),
                isSaved: true,
                voteId: match.voteId,
                isExisting: true
              };

              const assistantMessage = {
                role: "assistant",
                content: `I found an existing ballot matching your search: "${match.title}".`,
                parsed: {
                  text: `I found an existing ballot matching your search: "${match.title}".`,
                  hasBallots: true,
                  ballots: [matchedBallot]
                }
              };

              setMessages((prev) => [...prev, assistantMessage]);
              setIsLoading(false);

              // Save this interaction to chatbot history too so it persists
              const historyPayload = {
                userId: userId,
                sessionId: sessionId,
                title: sessionId ? null : userMessage.substring(0, 50),
                messageText: JSON.stringify({
                  user: userMessage,
                  ai: assistantMessage.content,
                  ballots: [matchedBallot]
                })
              };
              
              try {
                const historyResponse = await saveChatbotHistory(historyPayload);
                const savedHistoryId = historyResponse?.data?.historyId || historyResponse?.historyId;
                if (savedHistoryId) {
                  setMessages(prev => prev.map((msg, idx) =>
                    idx === prev.length - 1 ? { ...msg, historyId: savedHistoryId } : msg
                  ));
                }
                if (!sessionId) {
                  const newSessionId = historyResponse?.data?.sessionId || historyResponse?.sessionId;
                  if (newSessionId) {
                    setSessionId(newSessionId);
                  }
                }
              } catch (historyError) {
                console.error("Failed to save chatbot history for matched ballot:", historyError);
              }
              return; // Exit handleSend early!
            }
          }
        }
      } catch (searchError) {
        console.warn("Failed to search existing votes, falling back to AI generation:", searchError);
      }

      // 1. Get prepared prompt messages from backend
      // API expects: { userId, sessionId, newMessage }
      const promptPayload = {
        userId: userId,
        sessionId: sessionId,
        newMessage: userMessage
      };

      const promptResponse = await preparePromptMainBallot(promptPayload);

      let aiMessages = [];
      if (promptResponse && promptResponse.success && Array.isArray(promptResponse.data)) {
        aiMessages = promptResponse.data;
      } else {
        throw new Error("Failed to prepare prompt from backend");
      }

      // 2. Ensure we have Azure Credentials
      let currentCredentials = azureCredentials;
      if (!currentCredentials) {
        console.warn("Azure credentials missing in state, fetching now...");
        const credResponse = await getAzureCredentials();
        if (credResponse && credResponse.success && credResponse.data) {
          currentCredentials = credResponse.data;
          setAzureCredentials(credResponse.data);
        } else {
          throw new Error("Could not fetch Azure credentials");
        }
      }

      // 3. Call Azure OpenAI directly from frontend
      const responseText = await callAzureOpenAI(currentCredentials, aiMessages);

      // 4. Parse and display
      const parsed = parseBallotResponse(responseText);

      // Add assistant response
      const assistantMessage = {
        role: "assistant",
        content: responseText,
      };

      if (parsed && parsed.hasBallots) {
        assistantMessage.parsed = parsed;
      }

      setMessages((prev) => [
        ...prev,
        assistantMessage,
      ]);

      // 5. Save history to backend
      if (userId) {
        // Prepare payload with strict format: {"user": "...", "ballots": [...]}
        const historyPayload = {
          userId: userId,
          sessionId: sessionId,
          title: sessionId ? null : userMessage.substring(0, 50),
          messageText: JSON.stringify({
            user: userMessage,
            ai: parsed.text, // Cleaned text with __BALLOT_X__ placeholders (prevents duplicate rendering)
            ballots: parsed.ballots.map(b => ({
              ...b,
              isSaved: false, // Always false in DB - tracked client-side only
              options: b.options.map(opt => ({ ...opt })) // Deep clone options too
            }))
          })
        };

        try {
          console.log("💾 Saving chat history with payload:", historyPayload);
          const historyResponse = await saveChatbotHistory(historyPayload);
          console.log("✅ Chat history save response:", historyResponse);

          // Capture historyId from response (prioritize data.historyId based on API spec)
          const savedHistoryId = historyResponse?.data?.historyId || historyResponse?.historyId;
          console.log("📍 Extracted historyId:", savedHistoryId);

          // Update the message in state with valid historyId
          if (savedHistoryId) {
            setMessages(prev => prev.map((msg, idx) =>
              idx === prev.length - 1 ? { ...msg, historyId: savedHistoryId } : msg
            ));
            console.log("✓ Updated last message with historyId:", savedHistoryId);
          } else {
            console.warn("⚠️ No historyId found in response!");
          }

          // If this was a new session, capture sessionId from response
          if (!sessionId) {
            const newSessionId = historyResponse?.data?.sessionId || historyResponse?.sessionId;
            console.log("📍 Extracted new sessionId:", newSessionId);

            if (newSessionId) {
              setSessionId(newSessionId);
              console.log("✓ Updated sessionId:", newSessionId);
            }
          }
        } catch (historyError) {
          console.error("Failed to save chatbot history:", historyError);
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);

      let errorMessage = "Sorry, I encountered an error while processing your request. Please try again.";

      // Handle Azure OpenAI content filtering specifically
      if (error?.response?.data?.error?.code === "content_filter") {
        errorMessage = "Your message was flagged by our safety system because it contains inappropriate or unsafe content. Please try rephrasing your request.";
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: errorMessage,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // When user clicks on one of the suggested trending questions
  const handleTrendingClick = (question) => {
    if (!question || isLoading) return;
    // Directly send the selected question as the user's message,
    // similar to Figma flow where tapping a suggestion fires it.
    handleSend(question);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = () => {
    setMessages([initialGreeting]);
    setSelectedBallotId(null);
    setInputValue("");
    setSavedBallotIds(new Set()); // Reset saved ballots tracking
    // Reset sub-ballot states
    setIsEditingSubBallots(false);
    setEditableSubBallots([]);
    setSubBallotsSaved(false);
    // Reset session
    setSessionId(null);
    // Refresh trending questions for a fresh experience
    loadTrendingQuestions();
  };

  const renderChatbotBody = () => (
    <div className={`chatbot-screen ${isPageMode ? "chatbot-page-screen" : ""}`} onClick={(e) => e.stopPropagation()}>
      <div className="chatbot-header">
        <button className="chatbot-menu-btn" onClick={toggleSidebar}>
          <IoMenu />
        </button>
        <h3>Mate AI</h3>
        <div className="chatbot-header-actions">
          <button
            type="button"
            className="chatbot-clear-btn"
            onClick={handleClearChat}
          >
            Clear
          </button>
          {!isPageMode && (
            <button
              className="chatbot-close-btn"
              onClick={toggleChatbot}
              aria-label="Close Chatbot"
              type="button"
            >
              <IoClose className="close-icon" />
            </button>
          )}
        </div>
      </div>

      {showSidebar && (
        <div
          className="chatbot-sidebar-backdrop"
          onClick={() => setShowSidebar(false)}
        />
      )}
      <div className={`chatbot-sidebar ${showSidebar ? "open" : ""}`}>
        <button className="new-chat-btn" onClick={handleNewChat}>
          <span>+</span> New Chat
        </button>
        <div className="chatbot-history-list">
          <div className="history-section-title">Your conversations</div>
          {isHistoryLoading ? (
            <div className="history-loading">Loading...</div>
          ) : (
            chatHistoryList.map((session) => {
              let displayTitle = session.title;

              if ((!displayTitle || displayTitle === "New Conversation") && session.messages && session.messages.length > 0) {
                try {
                  const sortedMsgs = [...session.messages].sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
                  const firstMsg = sortedMsgs[0];

                  if (firstMsg && firstMsg.messageText) {
                    const parsed = JSON.parse(firstMsg.messageText);
                    if (parsed && parsed.user) {
                      displayTitle = parsed.user.length > 40 ? parsed.user.substring(0, 40) + "..." : parsed.user;
                    }
                  }
                } catch (e) {}
              }

              return (
                <div
                  key={session.sessionId}
                  className={`history-item ${sessionId === session.sessionId ? 'active' : ''}`}
                  onClick={() => handleSelectSession(session)}
                  title={displayTitle || "New Conversation"}
                >
                  <div className="history-item-title">
                    {displayTitle || "New Conversation"}
                  </div>
                  <div className="history-item-date">
                    {new Date(session.lastMessageOn).toLocaleDateString()}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="chatbot-body">
        {/* Trending / Hot topics section */}
        <div className="chatbot-trending">
          <div className="chatbot-trending-header">
            <h4 className="chatbot-trending-title">Ballot Search & Generator</h4>
            <p className="chatbot-trending-subtitle">
              Search for existing ballots or generate new voting questions on any topic
            </p>
          </div>
          <div className="chatbot-trending-list">
            {isTrendingLoading && (
              <div className="chatbot-trending-placeholder">
                <span className="trending-skeleton" />
                <span className="trending-skeleton" />
                <span className="trending-skeleton" />
              </div>
            )}
            {!isTrendingLoading &&
              trendingQuestions?.slice(0, 3).map((item, idx) => (
                <button
                  key={idx}
                  className="chatbot-trending-item"
                  type="button"
                  onClick={() => handleTrendingClick(item.question)}
                  disabled={isLoading}
                  title={item.question}
                >
                  {item.question}
                </button>
              ))}
          </div>
        </div>

        <div className="chatbot-messages">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`chatbot-message ${message.role === "user" ? "user-message" : "bot-message"} ${message.parsed ? "has-ballots" : ""}`}
            >
              {(() => {
                const isLast = index === messages.length - 1;
                const canShowButtons = isLast && !message.fromHistory;

                if (message.parsed && (message.parsed.hasBallots || (message.parsed.text && message.parsed.text.trim()))) {
                  return formatMessage(message.parsed.text, message.parsed.ballots, index, canShowButtons);
                }

                return message.content;
              })()}
            </div>
          ))}
          {isLoading && (
            <div className="chatbot-message bot-message loading-message">
              <p>Thinking</p>
              <div className="loading-dots">
                <div className="loading-dot"></div>
                <div className="loading-dot"></div>
                <div className="loading-dot"></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {showSubBallotPrompt && (
          isGeneratingSubBallots ? (
            <div className="subballot-generating">
              <div className="generating-spinner"></div>
              <p>Generating sub-ballots for each option...</p>
            </div>
          ) : (
            <div className="subballot-prompt">
              <p className="subballot-question">
                Do you want to generate sub-ballots for each option?
              </p>
              <div className="subballot-actions">
                <button
                  className="subballot-btn yes"
                  onClick={handleSubBallotYes}
                  disabled={isGeneratingSubBallots}
                >
                  Yes
                </button>
                <button
                  className="subballot-btn no"
                  onClick={handleSubBallotNo}
                  disabled={isGeneratingSubBallots}
                >
                  No
                </button>
              </div>
            </div>
          )
        )}
      </div>
      <div className="chatbot-input-container">
        <input
          ref={inputRef}
          type="text"
          className="chatbot-input"
          placeholder="Type your message..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
        />
        <button
          className="chatbot-send-btn"
          onClick={handleSend}
          disabled={isLoading || !inputValue.trim()}
        >
          Send
        </button>
      </div>
    </div>
  );

  if (isPageMode) {
    return (
      <>
        {renderChatbotBody()}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  // Floating button mode for non-add-ballot pages
  const isAddBallotPage =
    window.location.pathname === "/add-ballot" ||
    window.location.pathname === "/create-ballot";

  if (isAddBallotPage) {
    return null;
  }

  return (
    <div className="chatbot-icon-container">
      <button
        className="chatbot-icon-btn"
        onClick={() => navigate("/add-ballot")}
        title="Add Ballot"
        aria-label="Add Ballot"
      >
        <BsChatDotsFill className="chatbot-icon" />
      </button>
    </div>
  );
};

export default Chatbot;

